import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  LogOut,
  User,
  CreditCard,
  ChevronDown,
  Receipt,
  Users,
  ExternalLink
} from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

interface SettingsDropdownProps {
  userEmail?: string;
  userName?: string;
  isDemoUser?: boolean;
  virtualAssistantAvailable?: boolean;  // Show Team Management only when true
  lightMode?: boolean;  // Light mode styling
  isStaff?: boolean;  // VH Staff users have limited menu (no subscription/billing/settings)
}

const SettingsDropdown: React.FC<SettingsDropdownProps> = ({
  userEmail = 'User',
  userName = '',
  isDemoUser = false,
  virtualAssistantAvailable = false,
  lightMode = false,
  isStaff = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to invalidate session
      const token = Cookies.get('auth_token');
      if (token) {
        try {
          await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/v1/auth/logout`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('[Logout] Backend session invalidated');
        } catch (backendError) {
          console.warn('[Logout] Backend logout failed:', backendError);
          // Continue with local cleanup even if backend fails
        }
      }

      // Clear all auth-related cookies
      Cookies.remove('auth_token');
      Cookies.remove('demo_token');
      Cookies.remove('refresh_token');

      // Clear all localStorage items
      localStorage.removeItem('isDemo');
      localStorage.removeItem('demoPlan');
      localStorage.removeItem('demoAuthToken');
      localStorage.removeItem('current_rest_id');
      localStorage.removeItem('auth_provider');
      localStorage.removeItem('selected_plan');
      localStorage.removeItem('extraction_session_token');
      localStorage.removeItem('pending_extraction');
      localStorage.removeItem('extraction_items_count');
      localStorage.removeItem('extraction_was_cached');
      localStorage.removeItem('oauth_signup_flow');
      // Clear activation flow consent data
      localStorage.removeItem('activation_terms_accepted');
      localStorage.removeItem('activation_privacy_accepted');
      localStorage.removeItem('activation_email_opt_in');
      localStorage.removeItem('activation_session_token');

      // Clear session storage
      sessionStorage.clear();

      console.log('[Logout] All local data cleared');
      toast.success('Successfully logged out');

      // Hard redirect to home page (clears all React state)
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect even on error
      window.location.href = '/';
    }
  };

  // Generate initials from name or email
  const getInitials = () => {
    if (userName) {
      const names = userName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return userName.substring(0, 2).toUpperCase();
    }

    // Fallback to email
    if (userEmail && userEmail !== 'User') {
      const emailName = userEmail.split('@')[0];
      return emailName.substring(0, 2).toUpperCase();
    }

    return 'DU'; // Default User
  };

  // Handle Stripe billing portal redirect
  const handleViewBillingHistory = async () => {
    setIsOpen(false);
    try {
      toast.loading('Opening billing history...', { id: 'billing-redirect' });
      const response = await apiClient.post('/stripe/customer-portal');
      const portalUrl = response.data?.portal_url;

      // Security: Validate that the URL is a legitimate Stripe domain
      if (portalUrl && (
        portalUrl.startsWith('https://billing.stripe.com/') ||
        portalUrl.startsWith('https://checkout.stripe.com/')
      )) {
        toast.dismiss('billing-redirect');
        window.location.href = portalUrl;
      } else {
        console.error('Invalid portal URL received:', portalUrl);
        toast.error('Unable to open billing portal', { id: 'billing-redirect' });
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing history. Please try again.', { id: 'billing-redirect' });
    }
  };

  // Build menu items - conditionally based on user type
  // VH Staff users only see Profile and Logout (no admin settings)
  const menuItems = [
    {
      icon: User,
      label: 'Profile',
      action: () => {
        setIsOpen(false);
        navigate('/restaurant-settings#profile');
      }
    },
    // Admin-only items (hidden for VH Staff)
    ...(!isStaff ? [
      {
        icon: CreditCard,
        label: 'Subscription Plan',
        action: () => {
          setIsOpen(false);
          navigate('/restaurant-settings#subscription');
        }
      },
      {
        icon: ExternalLink,
        label: 'View Billing History',
        action: handleViewBillingHistory
      },
      // Team Management - ONLY show for Virtual Assistant (Waiter Staff) users
      ...(virtualAssistantAvailable ? [{
        icon: Users,
        label: 'Team Management',
        action: () => {
          setIsOpen(false);
          navigate('/restaurant-settings#team');
        }
      }] : []),
      {
        icon: Settings,
        label: 'Settings',
        action: () => {
          setIsOpen(false);
          navigate('/restaurant-settings#profile');
        }
      },
    ] : []),
    {
      icon: LogOut,
      label: 'Logout',
      action: () => {
        setIsOpen(false);
        handleLogout();
      }
    },
  ];

  return (
    <div className="relative">
      {/* Settings Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          lightMode ? 'bg-[#FDB913]' : 'bg-[#FDB913]'
        }`}>
          <span className="text-sm font-semibold text-black">
            {getInitials()}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${
          lightMode ? 'text-gray-400' : 'text-white/60'
        }`} />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`absolute right-0 mt-2 w-56 rounded-xl shadow-xl z-50 overflow-hidden ${
                lightMode
                  ? 'bg-white border border-gray-200'
                  : 'bg-white/10 backdrop-blur-md border border-white/20'
              }`}
            >
              {menuItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      console.log(`Settings menu clicked: ${item.label}`);
                      item.action();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                      lightMode
                        ? `text-gray-700 hover:bg-gray-50 ${index === menuItems.length - 1 ? 'border-t border-gray-100' : ''}`
                        : `text-white hover:bg-white/20 ${index === menuItems.length - 1 ? 'border-t border-white/20' : ''}`
                    }`}
                  >
                    <IconComponent className={`h-4 w-4 ${lightMode ? 'text-gray-500' : 'text-white/80'}`} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SettingsDropdown;